import {
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { UseGoogleSheet } from "../../../hooks/UseGoogleSheets";
import { ButtonSecondary } from "../../../components/ctas";

function normalizePos(clientX, clientY, rect) {
	const x = (clientX - rect.left) / rect.width;
	const y = (clientY - rect.top) / rect.height;
	return {
		x: Math.min(Math.max(x, 0), 1),
		y: Math.min(Math.max(y, 0), 1),
	};
}

/**
 * Helper to convert a google-spreadsheet Row into our circle shape.
 * Adjust the header names here if your sheet uses something different.
 */
function rowToCircle(row, { boardId } = {}) {
	if (!row) return null;

	const rowBoardId = row.get("BoardId") || row.get("BOARD_ID") || "default";
	const deleted =
		String(row.get("Deleted") || row.get("DELETED") || "").toLowerCase() ===
		"true";

	if (boardId && rowBoardId !== boardId) return null;
	if (deleted) return null;

	const x = parseFloat(row.get("X") ?? row.get("x"));
	const y = parseFloat(row.get("Y") ?? row.get("y"));
	const label = row.get("Label") ?? row.get("LABEL") ?? "";

	if (Number.isNaN(x) || Number.isNaN(y)) return null;

	// Prefer UUID if present, otherwise fall back to rowNumber
	const uuid = row.get("UUID") || row.get("Id") || row.get("ID");
	const id = uuid || `row-${row.rowNumber}`;

	return {
		id,
		row,
		x,
		y,
		label,
		boardId: rowBoardId,
	};
}

export default function MapBoard({
	spreadsheetId = process.env.REACT_APP_Map,
	boardId = "default",
}) {
	const boardRef = useRef(null);
	const lastFetchTime = useRef(0);
	const minFetchInterval = 2000; // Minimum 2 seconds between fetches
	const dragState = useRef({ isDragging: false, circleId: null, startX: 0, startY: 0 });

	// Hook from src/hooks/UseGoogleSheets.js
	const { loadSheet, addRow, setRow, saveRow } = UseGoogleSheet({
		spreadsheetId,
	});

	const [circles, setCircles] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [menu, setMenu] = useState(null); // { id, x, y }
	const [draggingCircle, setDraggingCircle] = useState(null);
	const [hoveredCircle, setHoveredCircle] = useState(null); // { id, x, y, label }
	const [searchTerm, setSearchTerm] = useState("");

	const refreshCircles = useCallback(async (force = false, retryCount = 0) => {
		// Throttle API calls to prevent rate limiting
		const now = Date.now();
		if (!force && now - lastFetchTime.current < minFetchInterval) {
			console.log("Skipping refresh - too soon since last fetch");
			return;
		}

		try {
			setLoading(true);
			setError("");
			lastFetchTime.current = now;
			const rows = await loadSheet(true); // force=true
			const next =
				Array.isArray(rows)
					? rows
						.map((row) => rowToCircle(row, { boardId }))
						.filter(Boolean)
					: [];
			setCircles(next);
		} catch (e) {
			console.error(e);

			// Check if it's a rate limit error (429)
			if (e.message && e.message.includes("429") && retryCount < 3) {
				const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
				setError(`Rate limit hit. Retrying in ${delay / 1000}s...`);
				setTimeout(() => {
					refreshCircles(force, retryCount + 1);
				}, delay);
			} else {
				setError("Failed to load circles from Sheet. Please wait a moment and refresh.");
			}
		} finally {
			if (retryCount === 0) {
				setLoading(false);
			}
		}
	}, [loadSheet, boardId]);

	useEffect(() => {
		refreshCircles();
	}, [refreshCircles]);

	const handleBoardClick = useCallback(
		async (e) => {
			if (!boardRef.current) return;
			const rect = boardRef.current.getBoundingClientRect();
			const { x, y } = normalizePos(e.clientX, e.clientY, rect);

			let label = window.prompt("Enter a number:", "");
			if (label == null) return;
			label = String(label).trim();
			if (!/^-?\d+(\.\d+)?$/.test(label)) {
				window.alert("Please enter a valid number.");
				return;
			}

			// Check for duplicate
			const isDuplicate = circles.some(c => c.label.toLowerCase() === label.toLowerCase());
			if (isDuplicate) {
				setSearchTerm(label);
				window.alert(`A circle with number "${label}" already exists!`);
				return;
			}

			// Generate a UUID if supported, otherwise simple timestamp-based fallback
			const uuid =
				(window.crypto &&
					window.crypto.randomUUID &&
					window.crypto.randomUUID()) ||
				`circle-${Date.now()}-${Math.random().toString(16).slice(2)}`;

			// Optimistically add to UI
			const optimisticCircle = {
				id: uuid,
				row: null, // Will be filled on next refresh
				x,
				y,
				label,
				boardId,
			};
			setCircles((prev) => [...prev, optimisticCircle]);

			try {
				// Adjust column names as needed to match your sheet headers
				const newRowData = {
					UUID: uuid,
					BoardId: boardId,
					X: x.toString(),
					Y: y.toString(),
					Label: label,
					Deleted: "", // soft-delete flag
				};

				await addRow(newRowData);

				// Refresh to get the actual row object from the sheet (throttled)
				await refreshCircles(true);
			} catch (err) {
				console.error(err);
				setError("Failed to save to Sheet.");
				// Rollback optimistic update
				setCircles((prev) => prev.filter((c) => c.id !== uuid));
			}
		},
		[boardId, addRow, refreshCircles]
	);

	const handleCirclePointerDown = useCallback((id, e) => {
		e.stopPropagation();

		const circle = circles.find(c => c.id === id);
		if (!circle) return;

		// Capture the position immediately before the timeout
		const target = e.currentTarget;
		const rect = target.getBoundingClientRect();
		const menuX = rect.left + rect.width / 2;
		const menuY = rect.top + rect.height;

		dragState.current = {
			isDragging: false,
			circleId: id,
			startX: e.clientX,
			startY: e.clientY,
			originalX: circle.x,
			originalY: circle.y,
			longPressTimer: setTimeout(() => {
				// Long press detected - show menu instead of dragging
				dragState.current.isDragging = false;
				setMenu({
					id,
					x: menuX,
					y: menuY,
					label: circle.label,
				});
			}, 600)
		};
	}, [circles]);

	const handleCircleMouseEnter = useCallback((id, e) => {
		const circle = circles.find(c => c.id === id);
		if (!circle) return;

		const target = e.currentTarget;
		const rect = target.getBoundingClientRect();

		setHoveredCircle({
			id,
			x: rect.left + rect.width / 2,
			y: rect.top - 10,
			label: circle.label,
		});
	}, [circles]);

	const handleCircleMouseLeave = useCallback(() => {
		setHoveredCircle(null);
	}, []);

	const handleCircleDoubleClick = useCallback((id, e) => {
		e.stopPropagation();

		// Cancel any ongoing drag or long press
		if (dragState.current.longPressTimer) {
			clearTimeout(dragState.current.longPressTimer);
		}
		dragState.current.isDragging = false;

		const circle = circles.find(c => c.id === id);
		if (!circle) return;

		// Get position for menu
		const target = e.currentTarget;
		const rect = target.getBoundingClientRect();
		const menuX = rect.left + rect.width / 2;
		const menuY = rect.top + rect.height;

		setMenu({
			id,
			x: menuX,
			y: menuY,
			label: circle.label,
		});
	}, [circles]);

	const handleCirclePointerMove = useCallback((e) => {
		if (!dragState.current.circleId || !boardRef.current) return;

		const moveDistance = Math.abs(e.clientX - dragState.current.startX) + Math.abs(e.clientY - dragState.current.startY);

		// If moved more than 5px, start dragging and cancel long press
		if (moveDistance > 5 && dragState.current.longPressTimer) {
			clearTimeout(dragState.current.longPressTimer);
			dragState.current.isDragging = true;
			setDraggingCircle(dragState.current.circleId);
		}

		if (!dragState.current.isDragging) return;

		const rect = boardRef.current.getBoundingClientRect();
		const { x, y } = normalizePos(e.clientX, e.clientY, rect);

		// Update circle position optimistically
		setCircles(prev => prev.map(c =>
			c.id === dragState.current.circleId ? { ...c, x, y } : c
		));
	}, []);

	const handleCirclePointerUp = useCallback(async () => {
		if (dragState.current.longPressTimer) {
			clearTimeout(dragState.current.longPressTimer);
		}

		if (dragState.current.isDragging && dragState.current.circleId) {
			// Save the new position to Google Sheets
			const circle = circles.find(c => c.id === dragState.current.circleId);
			if (circle && circle.row) {
				try {
					setRow(circle.row, "X", circle.x.toString());
					setRow(circle.row, "Y", circle.y.toString());
					saveRow(circle.row);
				} catch (err) {
					console.error(err);
					setError("Failed to update position in Sheet.");
					// Rollback to original position
					setCircles(prev => prev.map(c =>
						c.id === dragState.current.circleId
							? { ...c, x: dragState.current.originalX, y: dragState.current.originalY }
							: c
					));
				}
			}
		}

		setDraggingCircle(null);
		dragState.current = { isDragging: false, circleId: null, startX: 0, startY: 0 };
	}, [circles, setRow, saveRow]);

	// Add global pointer move and up listeners
	useEffect(() => {
		window.addEventListener('pointermove', handleCirclePointerMove);
		window.addEventListener('pointerup', handleCirclePointerUp);

		return () => {
			window.removeEventListener('pointermove', handleCirclePointerMove);
			window.removeEventListener('pointerup', handleCirclePointerUp);
		};
	}, [handleCirclePointerMove, handleCirclePointerUp]);

	// Close menu when clicking outside
	useEffect(() => {
		const handleClickOutside = () => {
			if (menu) {
				setMenu(null);
			}
		};

		if (menu) {
			document.addEventListener('click', handleClickOutside);
			return () => document.removeEventListener('click', handleClickOutside);
		}
	}, [menu]);

	const handleEdit = async (id) => {
		const current = circles.find((c) => c.id === id);
		if (!current || !current.row) return;

		let next = window.prompt("Change number:", current.label);
		if (next == null) return;
		next = String(next).trim();
		if (!/^-?\d+(\.\d+)?$/.test(next)) {
			window.alert("Please enter a valid number.");
			return;
		}

		try {
			setRow(current.row, "Label", next);
			await saveRow(current.row);

			setCircles((prev) =>
				prev.map((c) =>
					c.id === id
						? {
							...c,
							label: next,
						}
						: c
				)
			);
			setMenu(null);
		} catch (err) {
			console.error(err);
			setError("Failed to update in Sheet.");
		}
	};

	const handleDelete = async (id) => {
		const current = circles.find((c) => c.id === id);
		if (!current || !current.row) return;

		// Show confirmation dialog
		const confirmed = window.confirm(`Are you sure you want to delete circle ${current.label}?`);
		if (!confirmed) return;

		try {
			// Soft delete by setting Deleted = TRUE (or whatever your sheet expects)
			setRow(current.row, "Deleted", "TRUE");
			await saveRow(current.row);

			setCircles((prev) => prev.filter((c) => c.id !== id));
			setMenu(null);
		} catch (err) {
			console.error(err);
			setError("Failed to delete in Sheet.");
		}
	};

	const circleStyle = (c) => ({
		left: `${c.x * 100}%`,
		top: `${c.y * 100}%`,
	});

	const isSearchMatch = (circle) => {
		if (!searchTerm.trim()) return false;
		return circle.label.toLowerCase().includes(searchTerm.toLowerCase());
	};

	return (
		<div className="tw-w-full tw-h-screen tw-bg-black tw-flex tw-flex-col tw-justify-center tw-items-center">
			<div className="tw-absolute tw-top-4 tw-left-1/2 tw--translate-x-1/2 tw-z-10">
				<input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search VIN" className="tw-px-4 tw-py-2 tw-rounded-none tw-bg-white tw-border tw-border-gray-300 tw-text-black tw-placeholder-gray-500 tw-outline-none focus:tw-border-blue-500 tw-min-w-[300px]" />
			</div>
			<div className="tw-relative tw-w-full tw-max-w-[1400px] tw-mx-auto">
				<div ref={boardRef} className="tw-relative tw-w-full tw-bg-[#0b1220] tw-rounded-xl tw-overflow-hidden tw-border tw-border-[#1f2937]" onClick={handleBoardClick} role="application" aria-label="Numbered circle board" style={{ aspectRatio: "1400 / 920", backgroundImage: "url(/images/elkhart-office.png)", backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat" }}>
					{/* Grid overlay using inline backgroundImage instead of ::before */}
					<div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-opacity-40" style={{ backgroundImage: "linear-gradient(to right, #1f2a44 1px, transparent 1px), linear-gradient(to bottom, #1f2a44 1px, transparent 1px)", backgroundSize: "40px 40px" }} />

					{circles.map((c) => {
						const isMatch = isSearchMatch(c);
						return (
							<button key={c.id} className={`tw-absolute tw-rounded-full ${isMatch ? 'tw-bg-blue-700 tw-text-white' : 'tw-bg-white tw-text-[#111827]'} tw-grid tw-place-items-center tw-leading-none tw-font-bold tw-shadow-[0_8px_24px_rgba(0,0,0,0.35)] tw-border-2 tw-border-transparent tw--translate-x-1/2 tw--translate-y-1/2 tw-transform tw-select-none tw-touch-none hover:tw-brightness-95 hover:tw-cursor-move focus-visible:tw-outline-none focus-visible:tw-border-[#60a5fa] ${draggingCircle === c.id ? 'tw-cursor-grabbing tw-z-50' : ''}`} style={{ ...circleStyle(c), width: 'min(20px, 1.43vw)', height: 'min(20px, 1.43vw)', fontSize: 'min(10px, 0.71vw)' }} aria-label={`Circle ${c.label}`} onClick={(e) => e.stopPropagation()} onPointerDown={(e) => handleCirclePointerDown(c.id, e)} onDoubleClick={(e) => handleCircleDoubleClick(c.id, e)} onMouseEnter={(e) => handleCircleMouseEnter(c.id, e)} onMouseLeave={handleCircleMouseLeave}>
								<span className="tw-pointer-events-none tw-text-[.65em] tw-leading-none">
									{c.label}
								</span>
							</button>
						);
					})}

					{hoveredCircle && !draggingCircle && (
						<div className="tw-fixed tw--translate-x-1/2 tw--translate-y-full tw-bg-black/90 tw-backdrop-blur-xl tw-border tw-border-white/20 tw-rounded-lg tw-px-3 tw-py-2 tw-z-50 tw-pointer-events-none tw-shadow-lg" style={{ left: hoveredCircle.x, top: hoveredCircle.y }}>
							<span className="tw-text-2xl tw-font-bold tw-text-white">{hoveredCircle.label}</span>
						</div>
					)}

					{menu && (
						<div className="tw-fixed tw--translate-x-1/2 tw-translate-y-3 tw-bg-black/90 tw-backdrop-blur-2xl tw-border tw-border-black/20 tw-rounded-[10px] tw-p-2 tw-min-w-[160px] tw-z-20 tw-shadow-md" style={{ left: menu.x, top: menu.y }} onClick={(e) => e.stopPropagation()}>
							<div className="tw-text-center tw-mb-3 tw-pb-2 tw-border-b tw-border-white/20">
								<span className="tw-text-3xl tw-font-bold tw-text-white">{menu.label}</span>
							</div>
							<ButtonSecondary default className="tw-w-full tw-text-left" onClick={() => handleEdit(menu.id)}>
								<span className="tw-pointer-events-none">Edit</span>
							</ButtonSecondary>
							<ButtonSecondary default className="tw-w-full tw-text-left tw-mt-2" onClick={() => handleDelete(menu.id)}>
								<span className="tw-pointer-events-none">Delete</span>
							</ButtonSecondary>
							<ButtonSecondary default className="tw-w-full tw-text-left tw-mt-2" onClick={() => setMenu(null)}>
								<span className="tw-pointer-events-none">Close</span>
							</ButtonSecondary>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
